import React, { useState } from 'react';
import { create } from 'zustand';

export const useConfirmStore = create((set, get) => ({
  open: false,
  title: '',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  danger: false,
  details: null,
  inputLabel: null,       // if set, shows a text input; resolve receives the text or null
  inputPlaceholder: '',
  inputRequired: false,
  resolve: null,

  show(opts) {
    return new Promise(resolve =>
      set({ open: true, resolve, title: '', confirmLabel: 'Confirm', cancelLabel: 'Cancel', danger: false, details: null, inputLabel: null, inputPlaceholder: '', inputRequired: false, ...opts })
    );
  },
  answer(result) {
    const { resolve } = get();
    set({ open: false, resolve: null });
    resolve?.(result);
  },
}));

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 };
const card    = { background: 'var(--bg-card,#0f1629)', border: '1px solid var(--border,rgba(255,255,255,0.08))', borderRadius: 14, padding: 28, width: 440, maxWidth: '95vw', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' };
const inp     = { background: 'var(--bg-input,#161e35)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-primary,#e2e8f0)', fontSize: 13, outline: 'none', width: '100%', fontFamily: 'DM Sans,sans-serif', boxSizing: 'border-box', resize: 'vertical', minHeight: 36 };

function mkBtn(variant) {
  const base = { padding: '9px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'DM Sans,sans-serif' };
  if (variant === 'danger')  return { ...base, background: 'rgba(240,81,110,0.14)', color: '#f0516e' };
  if (variant === 'primary') return { ...base, background: '#c9a84c', color: '#0a0f1e' };
  return { ...base, background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary,#e2e8f0)' };
}

export function ConfirmDialog() {
  const { open, title, message, confirmLabel, cancelLabel, danger, details, inputLabel, inputPlaceholder, inputRequired, answer } = useConfirmStore();
  const [inputValue, setInputValue] = useState('');

  React.useEffect(() => { if (open) setInputValue(''); }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    if (inputLabel) {
      if (inputRequired && !inputValue.trim()) return;
      answer(inputValue.trim() || true);
    } else {
      answer(true);
    }
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) answer(null); }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 28, flexShrink: 0, lineHeight: 1 }}>{danger ? '⚠️' : '❓'}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: danger ? '#f0516e' : 'var(--text-primary,#e2e8f0)', marginBottom: message ? 6 : 0 }}>
              {title || (danger ? 'Are you sure?' : 'Confirm')}
            </div>
            {message && <div style={{ fontSize: 13, color: 'var(--text-muted,#94a3b8)', lineHeight: 1.6 }}>{message}</div>}
          </div>
        </div>

        {details && (
          <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
            {details.map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', gap: 10, fontSize: 12, marginBottom: 5, alignItems: 'baseline' }}>
                <span style={{ color: 'var(--text-subtle,#718096)', minWidth: 120, flexShrink: 0 }}>{label}</span>
                <span style={{ color: 'var(--text-primary,#e2e8f0)', fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {inputLabel && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: 'var(--text-subtle,#718096)', marginBottom: 5 }}>
              {inputLabel}{inputRequired && <span style={{ color: '#f0516e' }}> *</span>}
            </div>
            <textarea
              style={{ ...inp, minHeight: 64 }}
              placeholder={inputPlaceholder}
              value={inputValue}
              autoFocus
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleConfirm(); }}
            />
            {inputRequired && !inputValue.trim() && <div style={{ fontSize: 11, color: '#f0516e', marginTop: 4 }}>Required</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: (details || inputLabel) ? 0 : 20 }}>
          <button style={mkBtn('ghost')} onClick={() => answer(null)}>{cancelLabel}</button>
          <button style={{ ...mkBtn(danger ? 'danger' : 'primary'), opacity: inputRequired && !inputValue.trim() ? 0.5 : 1 }} onClick={handleConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
